// Decides whether a create draft still describes the order that was persisted
// under its localId. Used when a create resolves late (unconfirmed attempt,
// replayed localId, edits during the request): equal content means the tab can
// simply become the order's view; different content means the user's cart must
// be preserved.
//
// Only user-owned content participates. `covers` is deliberately excluded: for
// a persisted order it is server-owned (the table session wins and the list
// endpoint rewrites it), so socket and snapshot payloads of the same order
// legitimately differ there. The table itself is user-owned and echoed as sent.
import { OrderType } from '@/enums/OrderEnums';
import {
  buildSelectedOptionsSignature,
  normalizeDeliveryCoord,
} from './order-lines';
import type { IPosCartLine, IPosDraft } from './pos-types';

export type IPosDraftContent = Pick<
  IPosDraft,
  | 'type'
  | 'tableId'
  | 'customerId'
  | 'clubMemberId'
  | 'customerData'
  | 'deliveryInfo'
  | 'userComment'
  | 'customFieldValues'
  | 'cartLines'
>;

export interface IOrderContentComparison {
  /** Cart lines are the same multiset (indexes and prices ignored). */
  linesSame: boolean;
  /** `linesSame` and every compared meta field is equal. */
  same: boolean;
}

const trimmed = (value: string | null | undefined): string =>
  (value ?? '').trim();

/**
 * Index- and price-insensitive multiset key of the cart: quantities are
 * aggregated per (item, comment, selected options). Every payload that feeds
 * the orders store joins `options.optionItem.option`, so option signatures
 * built from persisted items are comparable with the draft's.
 */
export function buildCartLinesContentKey(lines: IPosCartLine[]): string {
  const quantityByLine = new Map<string, number>();
  for (const line of lines) {
    const key = `${line.itemId}|${trimmed(line.userComment)}|${buildSelectedOptionsSignature(line.optionValues)}`;
    quantityByLine.set(key, (quantityByLine.get(key) ?? 0) + line.quantity);
  }
  return [...quantityByLine.entries()]
    .map(([key, quantity]) => `${key}#${quantity}`)
    .sort()
    .join(';');
}

function buildCustomFieldsKey(values: Record<string, string>): string {
  return Object.entries(values)
    .map(([key, value]) => [key, trimmed(value)] as const)
    .filter(([, value]) => value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

function buildMetaKey(content: IPosDraftContent): string {
  const parts = [
    content.type,
    String(content.customerId ?? null),
    String(content.clubMemberId ?? null),
    trimmed(content.customerData.name),
    trimmed(content.customerData.phone),
    trimmed(content.userComment),
    buildCustomFieldsKey(content.customFieldValues),
  ];
  if (content.type === OrderType.Seat) {
    parts.push(String(content.tableId ?? null));
  }
  if (content.type === OrderType.Delivery) {
    // The pin is stored as a double and echoed exactly as sent.
    parts.push(
      trimmed(content.deliveryInfo?.address),
      trimmed(content.deliveryInfo?.address2),
      trimmed(content.deliveryInfo?.addressComment),
      String(normalizeDeliveryCoord(content.deliveryInfo?.lat)),
      String(normalizeDeliveryCoord(content.deliveryInfo?.lng))
    );
  }
  return parts.join('\u0000');
}

export function compareOrderContent(
  a: IPosDraftContent,
  b: IPosDraftContent
): IOrderContentComparison {
  const linesSame =
    buildCartLinesContentKey(a.cartLines) ===
    buildCartLinesContentKey(b.cartLines);
  return {
    linesSame,
    same: linesSame && buildMetaKey(a) === buildMetaKey(b),
  };
}
